import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "@/lib/firebase";
import { collection as fsCollection, getDocs } from "firebase/firestore";
import { setDragData } from "@/dnd";
import { fetchScryfallCardFullById } from "@/components/scan/CardScanner";
import cx from "classnames";

export type CardLike = {
  id: string;
  name: string;
  image?: string | null;
  custom_image_url?: string | null;
  foil?: boolean;
  price?: number | null;
  for_sale?: boolean | null;
  for_trade?: boolean | null;
  status?: "sale" | "trade" | "keep" | null;
  scryfall_id?: string | null;
  // optional metadata for legality/oracle
  scryfall_full?: any | null;
};

export interface CardTileProps {
  c: CardLike;
  uid?: string | null;

  // Actions
  onMoveToBinder?: (id: string) => void;
  onAddToDeck?: (id: string) => void;
  onEdit?: (c: CardLike) => void;
  onDelete?: (c: CardLike) => void;

  // Behavior
  context?: "collection" | "deck" | "binder";
  showOracleOnHover?: boolean;
  highlightIllegal?: boolean;
  illegalReason?: string | null; // e.g., "Not legal in Commander"

  // A11y/labels
  titleSuffix?: string;

  // NEW
  actionsSide?: "left" | "right";
}

const offersCountCache: Map<string, number> = new Map();

export function FoilStyles() {
  return (
    <style>{`
      .foil-sheen {
        position: absolute;
        inset: 0;
        border-radius: 0.25rem;
        pointer-events: none;
        mix-blend-mode: screen;
        opacity: 0.35;
        background:
          conic-gradient(
            from 180deg at calc(var(--px, 50) * 1%) calc(var(--py, 50) * 1%),
            #ff00e6, #00eaff, #ffe600, #ff00e6
          ),
          linear-gradient(115deg, rgba(255,255,255,0.05), rgba(255,255,255,0));
        background-size: 200% 200%;
        background-position: calc(var(--px, 50) * 1%) calc(var(--py, 50) * 1%);
        transition: opacity .2s ease;
        will-change: background-position;
      }
      .group:hover .foil-sheen { opacity: 0.6; animation: foilShift 6s linear infinite alternate; }
      @keyframes foilShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `}</style>
  );
}

export function IllegalStyles() {
  return (
    <style>{`
      .illegal-card {
        box-shadow: 0 0 0 2px rgba(255,60,60,0.7), 0 6px 24px rgba(255,0,0,0.35);
        position: relative;
      }
      .illegal-card::after {
        content: "";
        position: absolute;
        inset: 0;
        box-shadow: inset 0 0 24px rgba(255,0,0,0.35);
        pointer-events: none;
        border-radius: 0.25rem;
      }
      /* "idle then jiggle" every 30s */
      @keyframes illegalJiggleCycle {
        0%, 96% { transform: translate3d(0,0,0) rotate(0deg); }
        97% { transform: translate3d(-1px,0,0) rotate(-0.4deg); }
        98% { transform: translate3d(2px,0,0) rotate(0.4deg); }
        99% { transform: translate3d(-2px,0,0) rotate(-0.4deg); }
        100% { transform: translate3d(0,0,0) rotate(0deg); }
      }
      .illegal-card .illegal-jiggle {
        animation: illegalJiggleCycle 30s ease-in-out infinite;
        will-change: transform;
      }

      /* Oracle overlay */
      .oracle-overlay {
        position: absolute;
        inset: 0;
        display: none;
        align-items: center;
        justify-content: center;
        text-align: left;
        padding: 10px;
        background: rgba(0,0,0,0.86);
        color: #fff;
      }
      .group:hover .oracle-overlay,
      .group:focus-within .oracle-overlay {
        display: flex;
      }
      .oracle-box {
        max-width: 92%;
        max-height: 92%;
        overflow: auto;
        background: rgba(18,18,18,0.9);
        border: 1px solid rgba(255,255,255,0.12);
        border-radius: 6px;
        padding: 10px;
      }
      .oracle-legalities {
        margin-top: 8px;
        display: grid;
        grid-template-columns: repeat(2,minmax(0,1fr));
        gap: 6px 12px;
        font-size: 11px;
      }
      .oracle-legalities .ok { color: #7dffa0; }
      .oracle-legalities .no { color: #ff6b6b; }
    `}</style>
  );
}

export function CardTile(props: CardTileProps) {
  const {
    c,
    uid,
    onMoveToBinder,
    onAddToDeck,
    onEdit,
    onDelete,
    context = "collection",
    showOracleOnHover = true,
    highlightIllegal = false,
    illegalReason,
    titleSuffix,
    actionsSide = "right",
  } = props;

  const priceText = typeof c.price === "number" && Number.isFinite(c.price) ? `$${c.price.toFixed(2)}` : "";
  const showFlags = !!c.for_sale || !!c.for_trade;
  const flags = `${c.for_sale ? "S" : ""}${c.for_trade ? "T" : ""}`;

  // Offers count (lazy)
  const [offers, setOffers] = useState<number | null>(() => (c.id && offersCountCache.has(c.id) ? offersCountCache.get(c.id)! : null));
  useEffect(() => {
    let cancelled = false;
    if (!uid || !c.id) return;
    if (offersCountCache.has(c.id)) {
      setOffers(offersCountCache.get(c.id)!);
      return;
    }
    (async () => {
      try {
        const snap = await getDocs(fsCollection(db, "users", uid, "cards", c.id, "offers"));
        if (cancelled) return;
        offersCountCache.set(c.id, snap.size);
        setOffers(snap.size);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [uid, c.id]);

  // Foil sheen parallax
  const foilRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const handleFoilMove = (e: React.MouseEvent) => {
    if (!foilRef.current) return;
    const el = foilRef.current;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const doSet = () => {
      el.style.setProperty("--px", `${Math.round(x * 100)}`);
      el.style.setProperty("--py", `${Math.round(y * 100)}`);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(doSet);
  };

  // Oracle overlay: derive from scryfall_full or fetch on demand
  const [oracle, setOracle] = useState<{ text: string | null; legalities: Record<string, string> | null } | null>(null);
  const needsOracle = showOracleOnHover && (!!c.scryfall_full || !!c.scryfall_id);
  const legalities = (c.scryfall_full?.legalities as Record<string, string> | undefined) ?? oracle?.legalities ?? null;
  const oracleText = (c.scryfall_full?.oracle_text as string | undefined) ?? oracle?.text ?? null;

  useEffect(() => {
    if (!needsOracle || oracle) return;
    let cancelled = false;
    (async () => {
      try {
        const sf = c.scryfall_full || (c.scryfall_id ? await fetchScryfallCardFullById(c.scryfall_id) : null);
        if (cancelled) return;
        if (sf) setOracle({ text: sf.oracle_text ?? null, legalities: sf.legalities ?? null });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [needsOracle, c.scryfall_full, c.scryfall_id, oracle]);

  const title = useMemo(() => [c.name, titleSuffix].filter(Boolean).join(" — "), [c.name, titleSuffix]);

  return (
    <article
      className={`rounded border border-gray-700 bg-black/30 overflow-hidden ${highlightIllegal ? "illegal-card" : ""}`}
      role="group"
      aria-label={c.name}
      title={title}
      draggable
      onDragStart={(e) => {
        setDragData(e, { userCardId: c.id });
        try {
          const img = (e.currentTarget as HTMLElement).querySelector("img");
          if (img) e.dataTransfer.setDragImage(img, Math.floor(img.clientWidth / 2), Math.floor(img.clientHeight / 2));
        } catch {}
      }}
    >
      <div
        className={`relative aspect-[3/4] bg-black flex items-center justify-center cursor-zoom-in group ${highlightIllegal ? "illegal-jiggle" : ""}`}
        onMouseMove={c.foil ? handleFoilMove : undefined}
        role="button"
      >
        <img
          src={c.custom_image_url || c.image || "/images/cardback.png"}
          alt={c.name}
          className="h-full w-full object-cover"
          loading="lazy"
          draggable
          onDragStart={(e) => setDragData(e, { userCardId: c.id })}
        />

        {c.foil && <div ref={foilRef} className="foil-sheen pointer-events-none" />}

        {/* Top-right action buttons */}
        <div
          className={cx(
            "absolute top-1 flex flex-col gap-1 z-20",
            actionsSide === "left" ? "left-1 items-start" : "right-1 items-end"
          )}
        >
          {onMoveToBinder && (
            <button
              type="button"
              className="p-1.5 rounded-md bg-black/60 hover:bg-black/70 text-white shadow border border-white/10"
              aria-label="Move to binder"
              title="Move to binder"
              onClick={(e) => { e.stopPropagation(); onMoveToBinder(c.id); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h14a2 2 0 0 1 2 2v9H6a2 2 0 0 1-2-2V6z" stroke="currentColor" strokeWidth="2" />
                <path d="M4 6V4h10l2 2H6l2-2z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          )}
          {onAddToDeck && (
            <button
              type="button"
              className="p-1.5 rounded-md bg-black/60 hover:bg-black/70 text-sky-300 shadow border border-white/10"
              aria-label="Add to deck"
              title="Add to deck"
              onClick={(e) => { e.stopPropagation(); onAddToDeck(c.id); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 3h8l2 4H6l2-4z" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 10v6m-3-3h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              className="p-1.5 rounded-md bg-black/60 hover:bg-black/70 text-yellow-300 shadow border border-white/10"
              aria-label="Edit"
              title="Edit"
              onClick={(e) => { e.stopPropagation(); onEdit(c); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4">
                <path
                  fill="currentColor"
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                />
              </svg>
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="p-1.5 rounded-md bg-black/60 hover:bg-black/70 text-red-400 shadow border border-white/10"
              aria-label="Remove"
              title="Remove"
              onClick={(e) => { e.stopPropagation(); onDelete(c); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4">
                <path d="M6 18L18 6M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Oracle overlay */}
        {showOracleOnHover && (oracleText || legalities || illegalReason) ? (
          <div className="oracle-overlay">
            <div className="oracle-box">
              <div className="text-center font-semibold text-white mb-2">{c.name}</div>
              {illegalReason ? (
                <div className="text-center text-red-400 font-semibold mb-2">{illegalReason}</div>
              ) : null}
              {oracleText ? <div className="text-sm whitespace-pre-wrap">{oracleText}</div> : null}
              {legalities ? (
                <>
                  <div className="text-center mt-3 font-semibold">Format Legalities</div>
                  <div className="oracle-legalities">
                    {Object.entries(legalities).map(([fmt, val]) => (
                      <div key={fmt}>
                        <span className="opacity-70">{fmt}</span>{" "}
                        <span className={val === "legal" ? "ok" : "no"}>{val}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <div className="px-2 py-1 text-xs text-slate-200 truncate">
        {c.name}
        {showFlags ? (
          <span className="ml-2 text-[10px] rounded px-1 py-[1px] bg-black/40 border border-white/10">{flags}</span>
        ) : null}
        {priceText ? <span className="ml-2 text-[10px] text-slate-300">{priceText}</span> : null}
        {offers != null && offers > 0 ? (
          <span className="ml-2 text-[10px] text-cyan-300">{offers} offers</span>
        ) : null}
      </div>
    </article>
  );
}
